import { OAuthService } from '../OAuthService';
import {
  createHttpClient,
  createRequestBuilder,
  HttpRequest,
  HttpResponse,
  Error,
  createError,
  ErrorCodes,
  Logger,
  createLogger,
  isError,
} from '@quickplay/foundation';
import { OAuthConstants } from './OAuthConstants';
import { OAuthRequest } from './OAuthRequest';
import { OAuthResponse, OAuthResponseConstants } from './OAuthResponse';
import { AuthorizationData } from '../model/AuthorizationData';
import { PlatformErrorCodes } from '../../PlatformErrorCodes';

const PLATFORM_SERVICE_ACCESS_TOKEN_FETCH_FAILURE_MSG = 'AccessToken fetch failed';
const INVALID_SERVER_RESPONSE = 'Invalid server response';
const CONTENT_TYPE_HEADER = 'Content-Type';
const CONTENT_TYPE_VALUE = 'application/x-www-form-urlencoded;charset=UTF-8';

export function OAuthServiceImpl(defaultTokenEndPointURL: string): OAuthService {
  const logger: Logger = createLogger();
  const oAuthService: OAuthService = {
    authorize: authorize,
  };

  async function authorize(clientID: string, clientSecret: string): Promise<AuthorizationData | Error> {
    try {
      const result = await buildAuthorizeCall(clientID, clientSecret);
      return processAuthorizationResponse(result);
    } catch (error) {
      return processAuthorizationError(error as Error);
    }
  }

  async function buildAuthorizeCall(clientID: string, clientSecret: string): Promise<HttpResponse<OAuthResponse>> {
    const authorizationRequest: OAuthRequest = {
      [OAuthConstants.PARAM_KEY_CLIENT_ID]: clientID,
      [OAuthConstants.PARAM_KEY_CLIENT_SECRET]: clientSecret,
      [OAuthConstants.PARM_KEY_GRANT_TYPE]: OAuthConstants.PARAM_VALUE_GRANT_TYPE_CLIENT_CREDENTIALS,
      [OAuthConstants.PARAM_KEY_AUDIENCE]: OAuthConstants.PARAM_VALUE_AUDIENCE,
      [OAuthConstants.PARAM_KEY_SCOPE]: OAuthConstants.PARAM_VALUE_SCOPE,
    };
    // For some reason this is not working in browsers
    // https://github.com/github/fetch/issues/263
    // const formData = new FormData();

    // Object.entries(authorizationRequest).forEach(([key, value]) => {
    //   formData.append(key, value);
    // });
    const postData = Object.entries(authorizationRequest)
      .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
      .join('&');
    const request: HttpRequest<string> = buildAuthorizationRequest(postData);
    return createHttpClient<string, OAuthResponse>().callWith(request);
  }

  function processAuthorizationResponse(result: HttpResponse<OAuthResponse>): AuthorizationData | Error {
    switch (result.isSuccessful) {
      case true: {
        return validateAuthResponse(result.body);
      }
      case false: {
        throw createError(
          PlatformErrorCodes.PLATFORM_SERVICE_ACCESS_TOKEN_FAILURE,
          PLATFORM_SERVICE_ACCESS_TOKEN_FETCH_FAILURE_MSG,
        );
      }
    }
  }

  function processAuthorizationError(error: Error): Promise<Error> {
    logger.error('Authorization error', error);
    if (isError(error)) {
      return Promise.reject(error);
    }

    return Promise.reject(
      createError(PlatformErrorCodes.PLATFORM_SERVICE_ERROR, PLATFORM_SERVICE_ACCESS_TOKEN_FETCH_FAILURE_MSG),
    );
  }

  function validateAuthResponse(authResponse: OAuthResponse | any): AuthorizationData | Error {
    if (!authResponse) {
      throw createError(
        PlatformErrorCodes.PLATFORM_SERVICE_MALFORMED_RESPONSE | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
        INVALID_SERVER_RESPONSE,
      );
    }
    const oAuthResponse: OAuthResponse = {
      accessToken: authResponse[OAuthResponseConstants.ACCESS_TOKEN],
      tokenExpiresInSeconds: authResponse[OAuthResponseConstants.EXPIRES_IN],
      tokenType: authResponse[OAuthResponseConstants.TOKEN_TYPE],
      error: authResponse[OAuthResponseConstants.ERROR],
      errorDescription: authResponse[OAuthResponseConstants.ERROR_DESCRIPTION],
    };

    if (!oAuthResponse.accessToken) {
      const serverError = {
        errorCode: oAuthResponse.error,
        errorMessage: `Server error message: ${oAuthResponse.errorDescription}`,
      };
      throw createError(
        PlatformErrorCodes.PLATFORM_SERVICE_MALFORMED_RESPONSE | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
        INVALID_SERVER_RESPONSE,
        undefined,
        JSON.stringify(serverError),
      );
    }

    return {
      accessToken: authResponse[OAuthResponseConstants.ACCESS_TOKEN],
      tokenType: authResponse[OAuthResponseConstants.TOKEN_TYPE],
      scope: authResponse[OAuthResponseConstants.SCOPE],
      tokenExpiresInSeconds: authResponse[OAuthResponseConstants.EXPIRES_IN],
      createdTime: Math.round(Date.now() / 1000),
    };
  }

  function buildAuthorizationRequest(postData: string): HttpRequest<string> {
    return createRequestBuilder<string>()
      .url(defaultTokenEndPointURL)
      .headers({ [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE })
      .post(postData)
      .build();
  }

  return oAuthService;
}
